import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';

import { setAfterLogin } from '@libraries/auth';
import { useUserProfile } from '@hooks/user';

import type { UserProfile } from '@steadioTypes/user';

type AccessControlProps = {
  children: React.ReactNode;
};

export default function AccessControl({ children }: AccessControlProps) {
  const router = useRouter();
  const { userProfile, loading, isSignedOut } = useUserProfile();
  const onceLoaded = useRef<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);

  const doWhenRouteChanged = useCallback(
    (asPath: string) => {
      if (loading) {
        return;
      }

      const fallbackUrl = getFallbackUrl({
        userProfile: userProfile as UserProfile,
        isSignedOut,
        pathname: asPath,
      });

      !fallbackUrl ? setIsValid(true) : location.replace(fallbackUrl);
    },
    [isSignedOut, loading, userProfile],
  );

  useEffect(() => {
    if (loading || onceLoaded.current) {
      return;
    }

    onceLoaded.current = true;
    doWhenRouteChanged(router.asPath);
  }, [loading, doWhenRouteChanged, router.asPath]);

  useEffect(() => {
    router.events.on('routeChangeStart', doWhenRouteChanged);
    return () => router.events.off('routeChangeStart', doWhenRouteChanged);
  }, [doWhenRouteChanged, router.events]);

  if (!needAcl(router.asPath)) {
    return <>{children}</>;
  }

  return <>{isValid ? children : ''}</>;
}

const blockingLoginUser = new Set([
  '/login',
  '/login/email-register',
  '/login/find-password',
  '/login/password-reset',
]);
const allowingUnverifiedUser = new Set(['/login/email-verification-guide']);
const allowingPreStepUserOnly = new Set(['/pre-step']);
const allowingAfterPreStepUserOnly = new Set([
  '/create-creator-account/required',
  '/create-creator-account/optional',
  '/create-creator-account/plans',
]);
const allowingCreatorOnly = new Set(['/creator-studio']);
const allowingLoginUserOnly = new Set(['/user']);
const allAccessControlUrls = new Set([
  ...blockingLoginUser,
  ...allowingUnverifiedUser,
  ...allowingPreStepUserOnly,
  ...allowingAfterPreStepUserOnly,
  ...allowingCreatorOnly,
  ...allowingLoginUserOnly,
]);

const needAcl = (pathname: string) => {
  return (
    Array.from(allAccessControlUrls).filter((aclUrl) =>
      pathname.startsWith(aclUrl),
    ).length > 0
  );
};

const getFallbackUrl = ({
  userProfile,
  isSignedOut,
  pathname,
}: {
  userProfile: UserProfile;
  isSignedOut: boolean;
  pathname: string;
}): string => {
  if (blockingLoginUser.has(pathname)) {
    return !isSignedOut ? '/' : '';
  }

  if (pathname.startsWith('/user/membership/detail')) {
    if (isSignedOut) {
      setAfterLogin({ redirectTo: pathname });
      return '/login';
    }
  }

  if (pathname.startsWith('/user/membership/join')) {
    if (isSignedOut) {
      setAfterLogin({ redirectTo: pathname });
      return '/login';
    }

    return userProfile?.state === 'UNVERIFIED'
      ? '/login/email-verification-guide'
      : '';
  }

  if (pathname.startsWith('/user')) {
    // allowing login user only
    return isSignedOut ? '/' : '';
  }

  if (pathname.startsWith('/creator-studio')) {
    // allowing creator only
    return userProfile?.role === 'CREATOR' ? '' : '/';
  }

  if (allowingUnverifiedUser.has(pathname)) {
    return userProfile?.state === 'UNVERIFIED' ? '' : '/';
  }

  const isVerifiedUser = userProfile?.state === 'VERIFIED';

  if (allowingPreStepUserOnly.has(pathname)) {
    return isVerifiedUser && !userProfile?.categorySlug ? '' : '/';
  }

  if (allowingAfterPreStepUserOnly.has(pathname)) {
    return isVerifiedUser && userProfile?.role === 'USER' ? '' : '/';
  }

  return '';
};
